import { useContext, useSyncExternalStore } from 'react';
import { ReactReduxContext } from 'react-redux';
import { INSTANCE_SLUGS } from '../instances/instanceSlugs';
import { isProfileInInstance } from '../instances/useInstance';
import type { RootState } from '../store/store';
import { selectIsMcGrawHillTextbook } from './mcgrawHill';
import { usePlainOraViewFlag } from './plainOraView';
import type { CyclingPlaceholderSpec } from '../components/ora/Chat/Companion/useCyclingPlaceholder';

/**
 * The chapter number as written in a file name ("Ch10.pdf" → "10",
 * "Chapter 01 - Title.pdf" → "01"); null when the name has none.
 */
export const mcgrawHillChapterNumber = (fileName: string): string | null =>
  /^\s*ch(?:apter)?[\s._-]*(\d+)/i.exec(fileName)?.[1] ?? null;

/**
 * mcgraw-hill: what the empty chat page's composer suggests, one ending at a
 * time, in place of Ora's (performance, a topic, how Ora works).
 */
export const MCGRAW_HILL_CHAT_PAGE_PLACEHOLDER: CyclingPlaceholderSpec = {
  lead: 'Ask about ',
  tails: ['a clinical case…', 'a diagnosis…', 'the latest literature…'],
};

/** mcgraw-hill: the chat's starter questions beside an open chapter. */
export const MCGRAW_HILL_CHAPTER_PROMPTS: readonly string[] = [
  'What does this chapter cover?',
  'What are the high yield points?',
];

/**
 * A chapter's label from its file name: "Ch10.pdf", "Ch 10.pdf" or
 * "Chapter 01 - Title.pdf" → "Chapter 10" / "Chapter 01", keeping the number as
 * written. Anything without a chapter number keeps the part before " - ".
 */
export const mcgrawHillChapterLabel = (fileName: string): string => {
  const chapter = mcgrawHillChapterNumber(fileName);
  if (chapter) return `Chapter ${chapter}`;
  return fileName.split(' - ')[0].trim();
};

/**
 * A chapter's document title: "Chapter 12 - <lecture name>" when the file
 * names a chapter and the lecture's name is known; otherwise the file name.
 */
export const mcgrawHillDocumentTitle = (
  fileName: string,
  lectureName: string | null | undefined
): string => {
  const label = mcgrawHillChapterLabel(fileName);
  const name = lectureName?.trim();
  if (!label.startsWith('Chapter ')) return fileName;
  return name ? `${label} - ${name}` : label;
};

const noStoreSubscribe = () => () => undefined;

/** A label's name that is only a chapter file name: "Ch04.pdf", "Ch04". */
const CHAPTER_FILE_NAME =
  /^\s*ch(?:apter)?[\s._-]*0*(\d+)(?:\.[a-z0-9]{1,5})?\s*$/i;

/**
 * mcgraw-hill: a source or document pill whose name is a chapter's file name
 * reads as the chapter: "Ch04.pdf · p. 3" → "Chapter 4 · p. 3", "Ch04" →
 * "Chapter 4". Any other label (a lecture's own name) is left as it is.
 */
export const mcgrawHillChapterSourceLabel = (label: string): string => {
  const [name, ...rest] = label.split(' · ');
  const chapter = CHAPTER_FILE_NAME.exec(name)?.[1];
  if (!chapter) return label;
  return [`Chapter ${chapter}`, ...rest].join(' · ');
};

const keepLabel = (label: string): string => label;

/**
 * For McGraw Hill members, `mcgrawHillChapterSourceLabel`; for everyone else
 * the label unchanged. Store-safe like `useMcGrawHillDocumentTitle`: a host
 * rendered without a store gets the label unchanged.
 */
export const useMcGrawHillSourceLabel = (): ((label: string) => string) => {
  const store = useContext(ReactReduxContext)?.store;
  // The plain Ora view (ORA-1268) flips the answer without a store change.
  usePlainOraViewFlag();

  const isMember = useSyncExternalStore(
    store ? store.subscribe : noStoreSubscribe,
    () =>
      store
        ? isProfileInInstance(
            (store.getState() as Partial<RootState>).user?.profile,
            INSTANCE_SLUGS.mcgrawHill
          )
        : false
  );

  return isMember ? mcgrawHillChapterSourceLabel : keepLabel;
};

/**
 * For a member studying a McGraw Hill textbook (`selectIsMcGrawHillTextbook`),
 * the viewer's title formatter for one lecture (its name read from the
 * lecture tree); undefined otherwise (any other exam too), so the
 * viewer shows the file name as it always has. Reads the store through its
 * context so a host rendered without one (the companion's tests) simply gets
 * no formatter; the snapshot is a string, so unrelated store updates do not
 * re-render.
 */
export const useMcGrawHillDocumentTitle = (
  lectureId: string | null | undefined
): ((fileName: string) => string) | undefined => {
  const store = useContext(ReactReduxContext)?.store;
  usePlainOraViewFlag(); // The plain Ora view (ORA-1268) flips it too.

  // '' = not on a textbook; otherwise "mh:" + the lecture's name (possibly empty).
  const snapshot = useSyncExternalStore(
    store ? store.subscribe : noStoreSubscribe,
    () => {
      if (!store) return '';
      const state = store.getState() as Partial<RootState>;
      if (!selectIsMcGrawHillTextbook(state)) return '';

      const name = lectureId
        ? state.lectureTree?.nodesById?.[lectureId]?.name
        : undefined;

      return `mh:${name ?? ''}`;
    }
  );

  if (!snapshot) return undefined;
  const lectureName = snapshot.slice(3);
  return (fileName: string) => mcgrawHillDocumentTitle(fileName, lectureName);
};
